<template>
    <div class="monaco-tabbed-editor bg-card rounded-lg shadow border border-border overflow-hidden flex">
        <!-- Vertical Tabs Sidebar -->
        <div class="w-64 border-r border-border bg-muted/5 flex flex-col">
            <!-- Sidebar Header -->
            <div class="p-3 py-3.5 border-b border-border">
                <h3 class="text-sm font-medium text-foreground">Transform Files</h3>
            </div>

            <!-- Vertical Tab List -->
            <div class="flex-1 overflow-y-auto">
                <div class="space-y-0">
                    <template v-for="group in groupedTabs" :key="group.id || 'ungrouped'">
                        <!-- Group Header -->
                        <GroupHeader v-if="group.title" :title="group.title" />

                        <!-- Tabs in Group -->
                        <div class="p-2 space-y-1">
                            <button
                                v-for="tab in group.tabs"
                                :key="tab.id"
                                @click="switchTab(tab.id)"
                                :class="[
                                    'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
                                    activeTab === tab.id
                                        ? 'bg-primary text-primary-foreground'
                                        : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                ]"
                                :aria-label="`Edit ${tab.label}`">
                                <TabButton :tab="tab" :is-active="activeTab === tab.id" :is-modified="isTabModified(tab.id)" />
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Editor Area -->
        <div class="flex-1 flex flex-col">
            <!-- Editor Header -->
            <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-border bg-muted/5 min-h-[42px]">
                <div class="flex items-center gap-2 min-w-0">
                    <component v-if="currentTab" :is="currentFileIcon" class="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span class="text-sm font-medium truncate">{{ currentTab?.label }}</span>
                    <div v-if="isTabModified(activeTab)" class="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
                </div>

                <EditorToolbar
                    :current-tab="currentTab"
                    :is-saving="isSaving"
                    :has-unsaved-changes="hasUnsavedChanges"
                    :has-template="hasTemplateForCurrentTab"
                    @load-template="loadTemplate"
                    @save="saveCurrentContent"
                    @close="$emit('close')" />
            </div>

            <!-- Editor Content -->
            <div class="flex-1 relative">
                <AppLoader v-if="loading" :force-loading="loading" class="h-168" />
                <VueMonacoEditor
                    v-else
                    :value="tabContents[activeTab] || ''"
                    :language="editorLanguage"
                    :path="activeTab"
                    :theme="monacoTheme"
                    style="height: undefined !important; width: auto !important"
                    class="h-168"
                    @change="(value) => onEditorChange(activeTab, value)"
                    @mount="onEditorMount" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';
import { debounce } from 'lodash';
import { useMonacoTheme } from '@/composables/useMonacoTheme';
import { FileCode, FileText, Key, Database, Share2, Braces } from 'lucide-vue-next';
import AppLoader from '@/components/AppLoader.vue';
import TabButton from './TabButton.vue';
import EditorToolbar from './EditorToolbar.vue';
import GroupHeader from './GroupHeader.vue';

// Props
const props = defineProps({
    tabs: {
        type: Array,
        required: true,
        validator: (tabs) => tabs.every((tab) => tab.id && tab.label && tab.type && typeof tab.content === 'string')
    },
    modelValue: {
        type: String,
        default: ''
    },
    autoSave: {
        type: Boolean,
        default: true
    },
    autoSaveDelay: {
        type: Number,
        default: 2000
    },
    loading: {
        type: Boolean,
        default: false
    },
    isSaving: {
        type: Boolean,
        default: false
    },
    language: {
        type: String,
        default: 'javascript'
    },
    templateMap: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['update:modelValue', 'save', 'content-change', 'load-template', 'tab-change', 'close']);

// Theme management
const { monacoTheme } = useMonacoTheme();

// Editor language
const editorLanguage = computed(() => props.language);

// State
const activeTab = ref(props.tabs[0]?.id || '');
const tabContents = ref({});
const modifiedTabs = ref(new Set());
let saveDebounced = null;
let editorInstance = null;

// Computed
const currentTab = computed(() => props.tabs.find((tab) => tab.id === activeTab.value));

const hasUnsavedChanges = computed(() => modifiedTabs.value.size > 0);

// Check if current tab has template available
const hasTemplateForCurrentTab = computed(() => {
    if (!currentTab.value) return false;
    return !!props.templateMap[currentTab.value.id];
});

// File icon for current tab
const currentFileIcon = computed(() => {
    if (!currentTab.value) return FileCode;

    const iconMap = {
        COMMON_TRANSFORM: FileCode,
        VALUE_TRANSFORM: FileText,
        KEY_TRANSFORM: Key,
        TOPIC_TRANSFORM: Share2,
        VALUE_SCHEMA_TRANSFORM: Database,
        KEY_SCHEMA_TRANSFORM: Braces
    };

    return iconMap[currentTab.value.type] || FileCode;
});

// Grouped tabs computed property
const groupedTabs = computed(() => {
    const groups = new Map();
    const ungroupedTabs = [];

    // Process each tab
    props.tabs.forEach((tab) => {
        if (tab.group && tab.group.id && tab.group.title) {
            // Tab has a group
            if (!groups.has(tab.group.id)) {
                groups.set(tab.group.id, {
                    id: tab.group.id,
                    title: tab.group.title,
                    tabs: []
                });
            }
            groups.get(tab.group.id).tabs.push(tab);
        } else {
            // Tab is ungrouped
            ungroupedTabs.push(tab);
        }
    });

    // Convert groups map to array and add ungrouped tabs if any
    const result = Array.from(groups.values());

    if (ungroupedTabs.length > 0) {
        result.push({
            id: null,
            title: null,
            tabs: ungroupedTabs
        });
    }

    return result;
});

// Initialize tab contents from props
const initializeTabContents = () => {
    // Clear existing state completely
    tabContents.value = {};
    modifiedTabs.value.clear();

    // Initialize with new tab contents
    const contents = {};
    props.tabs.forEach((tab) => {
        contents[tab.id] = tab.content || '';
    });
    tabContents.value = contents;

    // Preserve current active tab if it exists, otherwise fallback to first tab
    if (props.tabs.length > 0) {
        const currentActiveTabExists = props.tabs.some(tab => tab.id === activeTab.value);
        if (!currentActiveTabExists) {
            activeTab.value = props.tabs[0].id;
        }
    }
};

// Watch for prop changes
watch(
    () => props.tabs,
    () => {
        initializeTabContents();
    },
    { immediate: true, deep: true }
);

// Tab management
const switchTab = (tabId) => {
    if (activeTab.value !== tabId) {
        activeTab.value = tabId;
        emit('tab-change', tabId, currentTab.value);
    }
};

const isTabModified = (tabId) => {
    return modifiedTabs.value.has(tabId);
};

// Editor event handlers
const onEditorChange = (tabId, value) => {
    tabContents.value[tabId] = value;
    modifiedTabs.value.add(tabId);

    emit('content-change', tabId, value);
    emit('update:modelValue', value);

    // Trigger auto-save
    if (props.autoSave && saveDebounced) {
        saveDebounced();
    }
};

const onEditorMount = (editor, monaco) => {
    // Store editor instance for cleanup
    editorInstance = editor;

    // Intercept Ctrl+S / Cmd+S to trigger save in editor
    editor.onKeyDown((e) => {
        if (e.keyCode === monaco.KeyCode.KeyS && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            saveCurrentContent();
        }
    });

    // Add keyboard navigation support
    setupKeyboardNavigation(editor, monaco);
};

// Keyboard navigation setup
const setupKeyboardNavigation = (editor, monaco) => {
    // Ctrl+Tab / Ctrl+Shift+Tab for tab switching
    editor.onKeyDown((e) => {
        if (e.ctrlKey && e.keyCode === monaco.KeyCode.Tab) {
            e.preventDefault();
            const currentIndex = props.tabs.findIndex((tab) => tab.id === activeTab.value);
            let nextIndex;

            if (e.shiftKey) {
                // Previous tab
                nextIndex = currentIndex > 0 ? currentIndex - 1 : props.tabs.length - 1;
            } else {
                // Next tab
                nextIndex = currentIndex < props.tabs.length - 1 ? currentIndex + 1 : 0;
            }

            switchTab(props.tabs[nextIndex].id);
        }

        // Ctrl+1-6 for direct tab access
        if (e.ctrlKey && e.keyCode >= monaco.KeyCode.Digit1 && e.keyCode <= monaco.KeyCode.Digit6) {
            e.preventDefault();
            const tabIndex = e.keyCode - monaco.KeyCode.Digit1;
            if (tabIndex < props.tabs.length) {
                switchTab(props.tabs[tabIndex].id);
            }
        }
    });
};

// Actions
const saveCurrentContent = () => {
    if (hasUnsavedChanges.value) {
        emit('save', {
            activeTab: activeTab.value,
            contents: { ...tabContents.value },
            modifiedTabs: Array.from(modifiedTabs.value)
        });

        // Clear modified state after save
        modifiedTabs.value.clear();
    }
};

const loadTemplate = () => {
    // Mark tab as modified to enable Save button
    modifiedTabs.value.add(activeTab.value);

    // Emit the template load event
    emit('load-template', activeTab.value, currentTab.value);
};

// Lifecycle
onMounted(() => {
    // Set up autosave debouncing
    if (props.autoSave) {
        saveDebounced = debounce(() => {
            saveCurrentContent();
        }, props.autoSaveDelay);
    }
});

onUnmounted(() => {
    // Cancel debounced save
    if (saveDebounced) {
        saveDebounced.cancel();
    }

    // Dispose Monaco editor instance
    if (editorInstance) {
        editorInstance.dispose();
        editorInstance = null;
    }

    // Clear tab contents to prevent state persistence
    tabContents.value = {};
    modifiedTabs.value.clear();
});

// Expose methods for parent component
defineExpose({
    switchTab,
    saveCurrentContent,
    loadTemplate,
    getTabContent: (tabId) => tabContents.value[tabId],
    setTabContent: (tabId, content) => {
        tabContents.value[tabId] = content;
        modifiedTabs.value.add(tabId);
    },
    clearModifiedState: () => modifiedTabs.value.clear(),
    clearAllState: () => {
        tabContents.value = {};
        modifiedTabs.value.clear();
        if (editorInstance) {
            editorInstance.setValue('');
        }
    },
    isModified: hasUnsavedChanges
});
</script>

<style scoped>
.monaco-tabbed-editor {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Ensure proper focus handling for accessibility */
.monaco-tabbed-editor :deep(.monaco-editor .monaco-editor-background) {
    outline: none;
}

.monaco-tabbed-editor :deep(.monaco-editor:focus-within) {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
}
</style>
